import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { API_URL, IMG_URL } from '../utils/constants';
import Loader from '../components/Loader';
import { toast } from 'react-toastify';

const CourseVideos = () => {
  const { subjectId, type: urlType } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [videos, setVideos] = useState([]);
  const [fetchType, setFetchType] = useState('video'); // 'video' or 'pdf'
  const [showPdfModal, setShowPdfModal] = useState(false);
  const [currentPdfUrl, setCurrentPdfUrl] = useState(null);
  const [currentPdfName, setCurrentPdfName] = useState('');


  // Get params from navigation state (matching mobile app)
  const { id, type, name, category, flag } = location.state || {};
  
  // Use state id if available, otherwise use URL param
  const actualSubjectId = id || subjectId;
  const actualType = type || urlType;

  useEffect(() => {
    if (actualSubjectId) {
      fetchVideos();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actualSubjectId, fetchType]);

  // Close modal on ESC key
  useEffect(() => {
    const handleEscKey = (e) => {
      if (e.key === 'Escape' && showPdfModal) {
        setShowPdfModal(false);
      }
    };
    
    if (showPdfModal) {
      document.addEventListener('keydown', handleEscKey);
      document.body.style.overflow = 'hidden'; // Prevent scrolling when modal is open
    }
    
    return () => {
      document.removeEventListener('keydown', handleEscKey);
      document.body.style.overflow = 'unset';
    };
  }, [showPdfModal]);

  const fetchVideos = async () => {
    try {
      setLoading(true);
      
      // Build URL matching mobile app logic (lines 40-60 in Videobysubject)
      let url;
      
      if (actualType === 'demo') {
        url = `${API_URL}/demovideosNew/${actualSubjectId}/${fetchType}`;
      } else if (actualType === 'Free Videos') {
        url = `${API_URL}/getFreeVideosBySubjectNew/${actualSubjectId}/${fetchType}`;
      } else {
        // If category is undefined, use videosNew, else use getSubjectVideoNew
        url = category === undefined
          ? `${API_URL}/videosNew/${actualSubjectId}/${fetchType}`
          : `${API_URL}/getSubjectVideoNew/${category}/${actualSubjectId}/${fetchType}`;
      }

      console.log('Fetching videos from:', url, { actualSubjectId, actualType, category, flag });
      
      const response = await axios.get(url, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      console.log('Videos Response:', response.data);
      console.log('Fetch Type:', fetchType);
      
      if (response.status === 200 && response.data) {
        const items = response.data.data || [];
        console.log(`${fetchType === 'video' ? 'Videos' : 'PDFs'} loaded:`, items.length);
        if (items.length > 0) {
          console.log('First item sample:', items[0]);
        }
        setVideos(items);
      }
    } catch (error) {
      console.error('Error fetching videos:', error);
      toast.error(error.response?.data?.message || 'Failed to load videos');
    } finally {
      setLoading(false);
    }
  };

  const handleVideoClick = useCallback((video) => {
    console.log('Video clicked:', video);
    console.log('Video URL:', video.videos);
    
    if (!video.videos) {
      toast.error('Video URL not available');
      return;
    }
    
    // Check if disabled (flag === 1 means not purchased)
    if (flag === 1) {
      toast.error('Please purchase this course to watch videos');
      return;
    }
    
    // Navigate to video player/description page
    navigate(`/video-player/${video.id}`, {
      state: {
        video: video.videos,
        description: video.description,
        flag: flag,
        courseId: video.category,
        title: video.title,
      }
    });
  }, [navigate, flag]);

  const handlePdfView = useCallback((pdf) => {
    console.log('PDF View clicked:', pdf);
    console.log('PDF relative path:', pdf.pdfs);
    
    if (!pdf.pdfs) {
      toast.error('PDF URL not available');
      return;
    }
    
    // Check if disabled (flag === 1 means not purchased)
    if (flag === 1) {
      toast.error('Please purchase this course to view PDFs');
      return;
    }
    
    // Prepend IMG_URL to PDF path (matching mobile app ViewPdf logic)
    const fullPdfUrl = IMG_URL + pdf.pdfs;
    console.log('Full PDF URL:', fullPdfUrl);
    
    // Open PDF in modal
    setCurrentPdfUrl(fullPdfUrl);
    setCurrentPdfName(pdf.pdfs.split('/').pop() || 'Document');
    setShowPdfModal(true);
  }, [flag]);

  const handlePdfDownload = useCallback((pdfUrl) => {
    console.log('PDF Download clicked - relative path:', pdfUrl);
    
    if (!pdfUrl) {
      toast.error('PDF URL not available');
      return;
    }
    
    // Check if disabled (flag === 1 means not purchased)
    if (flag === 1) {
      toast.error('Please purchase this course to download PDFs');
      return;
    }
    
    // Prepend IMG_URL to PDF path (matching mobile app)
    const fullPdfUrl = IMG_URL + pdfUrl;
    console.log('Full PDF URL for download:', fullPdfUrl);
    
    // Trigger download
    try {
      const link = document.createElement('a');
      link.href = fullPdfUrl;
      link.download = pdfUrl.split('/').pop() || 'document.pdf';
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success('Download started');
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download PDF');
    }
  }, [flag]);

  if (loading) return <Loader />;

  // If not purchased (flag === 1), show purchase required message
  if (flag === 1) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto p-8">
          <div className="text-8xl mb-6">🔒</div>
          <h2 className="text-3xl font-bold text-gray-800 mb-4">
            Purchase Required
          </h2>
          <p className="text-gray-600 text-lg mb-8">
            Please purchase this course to watch videos and view PDFs.
          </p>
          <button
            onClick={() => navigate(-1)}
            className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-8 rounded-lg transition"
          >
            Go Back to Course
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Page Header */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white py-12">
        <div className="container">
          <h1 className="text-3xl sm:text-4xl font-bold mb-2">
            {name || 'Videos'}
          </h1>
          <p className="text-white/90">Select content to view</p>
          
          {/* Debug Info */}
        
        </div>
      </div>

      {/* Video/PDF Toggle Tabs */}
      <div className="bg-white border-b shadow-sm sticky top-0 z-10">
        <div className="container">
          <div className="flex gap-2 py-4">
            <button
              onClick={() => setFetchType('video')}
              className={`flex-1 py-3 px-6 rounded-lg font-bold transition ${
                fetchType === 'video'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              📹 VIDEOS
            </button>
            <button
              onClick={() => setFetchType('pdf')}
              className={`flex-1 py-3 px-6 rounded-lg font-bold transition ${
                fetchType === 'pdf'
                  ? 'bg-red-600 text-white'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              📄 PDFS
            </button>
          </div>
        </div>
      </div>

      {/* Content List */}
      <div className="py-8">
        <div className="container">
          {videos.length > 0 ? (
            <div className="space-y-4">
              {fetchType === 'video' ? (
                // Video List
                videos.map((video) => (
                  <div
                    key={video.id}
                    className={`bg-white rounded-lg shadow-md hover:shadow-xl transition-all overflow-hidden ${
                      flag === 1 ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
                    }`}
                    onClick={() => flag !== 1 && handleVideoClick(video)}
                  >
                    <div className="flex items-center p-4 gap-4">
                      {/* Video Thumbnail */}
                      <div className="w-32 h-20 bg-gray-200 rounded flex-shrink-0 flex items-center justify-center">
                        <span className="text-4xl">▶️</span>
                      </div>
                      
                      {/* Video Info */}
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-800 line-clamp-2">
                          {video.title}
                        </h3>
                      </div>
                      
                      {/* Play Button */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleVideoClick(video);
                        }}
                        disabled={flag === 1}
                        className="bg-green-600 hover:bg-green-700 text-white font-bold py-2 px-6 rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Play Now
                      </button>
                    </div>
                  </div>
                ))
              ) : (
                // PDF List
                videos.map((pdf) => (
                  <div
                    key={pdf.id}
                    className={`bg-white rounded-lg shadow-md hover:shadow-xl transition-all overflow-hidden ${
                      flag === 1 ? 'opacity-50' : ''
                    }`}
                  >
                    <div className="flex items-center p-4 gap-4">
                      {/* PDF Icon */}
                      <div className="w-32 h-20 bg-red-100 rounded flex-shrink-0 flex items-center justify-center">
                        <span className="text-4xl">📄</span>
                      </div>
                      
                      {/* PDF Info */}
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-800 line-clamp-2">
                          {pdf.pdfs || 'PDF Document'}
                        </h3>
                      </div>
                      
                      {/* Action Buttons */}
                      <div className="flex gap-2">
                        <button
                          onClick={() => handlePdfView(pdf)}
                          disabled={flag === 1}
                          className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-lg transition text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          View PDF
                        </button>
                        <button
                          onClick={() => handlePdfDownload(pdf.pdfs)}
                          disabled={flag === 1}
                          className="bg-purple-600 hover:bg-purple-700 text-white font-bold py-2 px-4 rounded-lg transition text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Download
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          ) : (
            <div className="text-center py-16">
              <div className="text-6xl mb-6">{fetchType === 'video' ? '📹' : '📄'}</div>
              <h3 className="text-2xl font-bold text-gray-700 mb-3">
                No {fetchType === 'video' ? 'Videos' : 'PDFs'} Available
              </h3>
              <p className="text-gray-500 mb-8">
                There are no {fetchType === 'video' ? 'videos' : 'PDF files'} for this subject yet.
              </p>
              <button 
                onClick={() => navigate(-1)} 
                className="btn btn-primary"
              >
                Go Back
              </button>
            </div>
          )}
        </div>
      </div>

      {/* PDF Viewer Modal */}
      {showPdfModal && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-75"
          onClick={() => setShowPdfModal(false)}
        >
          <div 
            className="relative w-full h-full max-w-7xl max-h-screen m-4 bg-white rounded-lg shadow-2xl flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 border-b bg-gray-800 text-white rounded-t-lg">
              <h3 className="text-lg font-semibold truncate flex-1">
                📄 {currentPdfName}
              </h3>
              <div className="flex gap-2">
                <a
                  href={currentPdfUrl}
                  download={currentPdfName}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-semibold transition"
                  onClick={(e) => e.stopPropagation()}
                >
                  ⬇ Download
                </a>
                <button
                  onClick={() => setShowPdfModal(false)}
                  className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg text-sm font-semibold transition"
                >
                  ✕ Close
                </button>
              </div>
            </div>
            
            {/* PDF Viewer */}
            <div className="flex-1 overflow-hidden">
              <iframe
                src={currentPdfUrl}
                className="w-full h-full"
                title={currentPdfName}
                frameBorder="0"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CourseVideos;

